// معادلة كلفة لوحة الكانفاس - مصدر واحد تستخدمه واجهة "حساب سعر اللوحة"
// (CanvasCostCalculator.jsx) وفورم إضافة طلب ارت تاتش (PartnerAddOrder.jsx)
// حتى يبقى نفس الرقم بالمكانين.

export const DEFAULT_CANVAS_UNIT_PRICE = 25000;

const ceilToStep = (value, step) => Math.ceil(value / step) * step;

// الطول والعرض بالسنتيمتر، unitPrice = سعر الطباعة للمتر (كانفاس)
export const computeCanvasCosts = (lengthCm, widthCm, unitPrice) => {
  const L = Number(lengthCm);
  const W = Number(widthCm);
  if (!Number.isFinite(L) || !Number.isFinite(W) || L <= 0 || W <= 0) return null;

  const printCost = ceilToStep((L / 100 + 0.05) * (W / 100 + 0.05), 0.2) * unitPrice;
  const baseDim = Math.ceil(((L / 100) + (W / 100)) * 2 + 0.5);
  const innerWood = baseDim * 2000;
  const ail = baseDim * 5500;
  const kas = baseDim * 7500;

  return { printCost, innerWood, ail, kas };
};

export const buildPriceRow = (title, sell) => ({
  title,
  origin: sell * 2.5,
  edition: (sell + 50000) * 2.25,
  prime: (sell + 75000) * 2.25,
});

/* ======================================================
   📊 قائمة الأسعار المرجعية الثابتة (القياس، Origin، Edition)
====================================================== */
export const frameData = [
  ["30 × 40", "45,000", "75,000"], ["40 × 60", "70,000", "115,000"],
  ["50 × 70", "85,000", "135,000"], ["60 × 80", "100,000", "165,000"],
  ["70 × 100", "125,000", "195,000"], ["80 × 120", "185,000", "275,000"],
  ["120 × 180", "295,000", "395,000"], ["160 × 240", "395,000", "515,000"],
  ["30 × 30", "40,000", "75,000"], ["40 × 40", "45,000", "85,000"],
  ["50 × 50", "60,000", "100,000"], ["60 × 60", "65,000", "115,000"],
  ["70 × 70", "95,000", "150,000"], ["80 × 80", "100,000", "165,000"],
  ["90 × 90", "115,000", "185,000"], ["100 × 100", "135,000", "215,000"],
  ["110 × 110", "175,000", "265,000"], ["120 × 120", "185,000", "275,000"],
  ["130 × 130", "215,000", "315,000"],
];

export const kasData = [
  ["30 × 40", "65,000", "95,000"], ["40 × 60", "75,000", "115,000"],
  ["50 × 70", "105,000", "165,000"], ["60 × 80", "140,000", "205,000"],
  ["70 × 100", "175,000", "245,000"], ["80 × 120", "245,000", "325,000"],
  ["120 × 180", "385,000", "495,000"], ["160 × 240", "495,000", "615,000"],
  ["30 × 30", "45,000", "75,000"], ["40 × 40", "60,000", "95,000"],
  ["50 × 50", "75,000", "115,000"], ["60 × 60", "95,000", "135,000"],
  ["70 × 70", "125,000", "175,000"], ["80 × 80", "150,000", "215,000"],
  ["90 × 90", "165,000", "235,000"], ["100 × 100", "195,000", "265,000"],
  ["110 × 110", "235,000", "315,000"], ["120 × 120", "265,000", "345,000"],
  ["130 × 130", "285,000", "375,000"],
];

const parseSize = (size) => size.replace(/\s/g, "").split("×").map(Number);
const parseIQD = (s) => Number(String(s).replace(/,/g, ""));

// صف الجدول المرجعي المطابق للقياس بالضبط (بالاتجاهين)، أو null - نفس
// مطابقة جداول Frame/Kas بواجهة حساب سعر اللوحة
const findStandardRow = (data, lengthCm, widthCm) => {
  const L = Number(lengthCm);
  const W = Number(widthCm);
  return (
    data.find(([size]) => {
      const [sw, sh] = parseSize(size);
      return (sw === L && sh === W) || (sw === W && sh === L);
    }) || null
  );
};

const FRAME_PRICE_SOURCES = {
  L: { label: "أيل", table: frameData, costKey: "ail" },
  Kas: { label: "كاس", table: kasData, costKey: "kas" },
};

// أسعار لوحة بإطار أيل/كاس بفئتي Origin/Edition - مطابقة لواجهة حساب سعر
// اللوحة: القياس الستاندرد ← جدول Frame (أيل) / Kas Frame (كاس) المرجعي،
// غيره ← صف المعادلة "طباعة + اطار داخلي خشب واطار خارجي أيل/كاس".
// تُستخدم بفورم "طلب جديد" مال ارت تاتش. id = "origin" | "edition"
export const getFramedPriceOptions = (lengthCm, widthCm, unitPrice, frameType) => {
  const source = FRAME_PRICE_SOURCES[frameType];
  const costs = computeCanvasCosts(lengthCm, widthCm, unitPrice);
  if (!source || !costs) return [];

  const row = findStandardRow(source.table, lengthCm, widthCm);
  if (row) {
    return [
      { id: "origin", label: `${source.label} – Origin`, price: parseIQD(row[1]) },
      { id: "edition", label: `${source.label} – Edition`, price: parseIQD(row[2]) },
    ];
  }

  const r = buildPriceRow("", costs.printCost + costs.innerWood + costs[source.costKey]);
  return [
    { id: "origin", label: `${source.label} – Origin`, price: Math.round(r.origin) },
    { id: "edition", label: `${source.label} – Edition`, price: Math.round(r.edition) },
  ];
};

// أسعار الكانفاس فقط (طباعة + إطار داخلي خشب) بفئتي Origin/Edition - نفس
// اللي تطلعه حاسبة ارت تاتش (/partner-price)، وتُستخدم بفورمي ارت تاتش
// (بيع مباشر + طلب جديد). id = "origin" | "edition"
export const getCanvasPriceOptions = (lengthCm, widthCm, unitPrice) => {
  const costs = computeCanvasCosts(lengthCm, widthCm, unitPrice);
  if (!costs) return [];

  const r = buildPriceRow("", costs.printCost + costs.innerWood);
  return [
    { id: "origin", label: "كانفاس – Origin", price: Math.round(r.origin) },
    { id: "edition", label: "كانفاس – Edition", price: Math.round(r.edition) },
  ];
};

// فلترة صفوف الجدول المرجعي حسب القياس المكتوب - نفس فلترة جداول Frame/Kas
// بواجهة حساب سعر اللوحة (طول+عرض ← مطابقة بالاتجاهين، واحد بس ← أي صف بيه)
export const filterPriceRowsBySize = (data, width, height) =>
  data.filter(([size]) => {
    const [w, h] = parseSize(size);
    if (width && height) return (w === Number(width) && h === Number(height)) || (w === Number(height) && h === Number(width));
    if (width) return w === Number(width) || h === Number(width);
    if (height) return w === Number(height) || h === Number(height);
    return true;
  });
